import { useEffect, useRef, useState } from "react";
import { showToast, showLoadingToast } from "utils/reusableToast";
import {
  useGetCopilotDashboardSettingsQuery,
  useUpdateCopilotDashboardSettingsMutation,
} from "state/rtkQuery/copilot/copilotQueries";

// Global, single config — not per-user, per-org, or per-email. There is exactly
// one dashboard configuration for the whole system; everyone who loads the
// dashboard sees the same visible sections and the same Deloitte overage rate.
// Editing is restricted on the frontend via an email allowlist (see
// DashboardSettingsMenu.jsx), not by scoping the data itself — the backend has
// no permission check on the PUT.

export const SECTION_LABELS = {
  stat_cards: "Stat Cards",
  pool_chart: "Pool Consumption Chart",
  model_table: "Consumption by Model",
  engineer_table: "Top Engineers Table",
  cost_center_chart: "Consumption by Cost Center",
  monthly_trend_chart: "AIC + Spend Monthly Trend",
  model_usage_trend_chart: "Usage Grouped by Model (Trend)",
  pool_coverage_trend_chart: "Pool Coverage Trend",
};

const DEFAULT_VISIBLE_SECTIONS = Object.fromEntries(
  Object.keys(SECTION_LABELS).map((key) => [key, true])
);

// Deloitte's negotiated overage rate ($/AIC) — stored centrally so it can be
// updated without a frontend deploy if the negotiated rate changes.
export const DEFAULT_DELOITTE_OVERAGE_RATE = 0.0064;

const SAVE_DEBOUNCE_MS = 500;

export const useDashboardSettings = () => {
  const { data, isLoading } = useGetCopilotDashboardSettingsQuery();
  const [updateSettings] = useUpdateCopilotDashboardSettingsMutation();

  const [visibleSections, setVisibleSections] = useState(DEFAULT_VISIBLE_SECTIONS);
  const [deloitteOverageRate, setDeloitteOverageRate] = useState(DEFAULT_DELOITTE_OVERAGE_RATE);
  const [isLoaded, setIsLoaded] = useState(false);
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    if (!data) return;
    // Merge over defaults so a newly-added section defaults to visible for
    // anyone whose saved record predates that section being added.
    setVisibleSections({ ...DEFAULT_VISIBLE_SECTIONS, ...data.visible_sections });
    setDeloitteOverageRate(data.deloitte_overage_rate ?? DEFAULT_DELOITTE_OVERAGE_RATE);
    setIsLoaded(true);
  }, [data]);

  const persist = async (nextVisibleSections, nextDeloitteOverageRate) => {
    const toastId = showLoadingToast({ title: "Saving dashboard settings..." });
    try {
      await updateSettings({
        visible_sections: nextVisibleSections,
        deloitte_overage_rate: nextDeloitteOverageRate,
      }).unwrap();
      showToast({ id: toastId, title: "Dashboard settings saved", type: "success" });
    } catch {
      showToast({
        id: toastId,
        title: "Failed to save dashboard settings",
        description: "Please try again.",
        type: "error",
      });
    }
  };

  const scheduleSave = (nextVisibleSections, nextDeloitteOverageRate) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(
      () => persist(nextVisibleSections, nextDeloitteOverageRate),
      SAVE_DEBOUNCE_MS
    );
  };

  const toggleSection = (key) => {
    setVisibleSections((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      scheduleSave(next, deloitteOverageRate);
      return next;
    });
  };

  const updateDeloitteOverageRate = (rate) => {
    setDeloitteOverageRate(rate);
    scheduleSave(visibleSections, rate);
  };

  const resetToDefault = () => {
    setVisibleSections(DEFAULT_VISIBLE_SECTIONS);
    setDeloitteOverageRate(DEFAULT_DELOITTE_OVERAGE_RATE);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    persist(DEFAULT_VISIBLE_SECTIONS, DEFAULT_DELOITTE_OVERAGE_RATE);
  };

  return {
    visibleSections,
    toggleSection,
    deloitteOverageRate,
    updateDeloitteOverageRate,
    resetToDefault,
    isLoaded: isLoaded || (!isLoading && !data),
  };
};
